import axios from "axios";

/**
 * Thin client for the CPT-recommender service (compose / analyze / recommend).
 *
 * Separate from RemotingService: different base URL (REACT_APP_RECOMMENDER_URL)
 * and `auth-key` auth instead of the staging OAuth bearer. In dev that base URL
 * is the same-origin "/recommender" path that setupProxy.js forwards to the
 * recommender, injecting auth-key server-side (so the token stays out of the
 * bundle). Set REACT_APP_RECOMMENDER_AUTH_KEY only when calling the recommender
 * directly (cross-origin) instead. Callback style mirrors RemotingService.
 */
const BASE_URL = process.env.REACT_APP_RECOMMENDER_URL;
const AUTH_KEY = process.env.REACT_APP_RECOMMENDER_AUTH_KEY;

const instance = axios.create({
    baseURL: BASE_URL,
    headers: AUTH_KEY ? {"auth-key": AUTH_KEY} : {},
});

// The service stamps every response with X-Request-Id and writes the call (inputs,
// prompts, output) to its event log under that id. Keeping the last id per endpoint
// is what lets the review strip say "this verdict is about *that* answer" instead of
// shipping a second, lossy copy of everything the clinician saw.
const lastRequestIds = {};

instance.interceptors.response.use(
    res => {
        const id = res.headers?.["x-request-id"];
        if (id) lastRequestIds[res.config.url] = id;
        return res;
    },
    err => {
        const id = err.response?.headers?.["x-request-id"];
        if (id && err.config?.url) lastRequestIds[err.config.url] = id;
        return Promise.reject(err);
    },
);

class RecommenderService {

    // Free clinician text -> validated structured EMR fields + assembled emr_json.
    // body: { text, record?, visitId? }
    compose = (body, successCallback = () => {}, failCallback = () => {}) => {
        instance.post("/emr/compose", body)
            .then(res => successCallback(res.data))
            .catch(err => failCallback(err));
    };

    // Claim-scrubber: EMR json -> {clean_claim_score, score_excluding_skipped, threshold,
    // ready, blocking_count, checked, codes_checked, findings, skipped_count, semantic}.
    // Pass semantic:false for the deterministic-only pass (no LLM call, instant) used by
    // the "Check EMR" badge on visit load. `skipped` carries the rules the clinician has
    // declared irrelevant to this patient ([{rule_id, reason}]) — they leave the asks and
    // the working score, and the reasons are logged with the call.
    // body: { record, visitId?, codes?, maxQuestions?, semantic?, skipped?, threshold? }
    analyze = (body, successCallback = () => {}, failCallback = () => {}) => {
        instance.post("/emr/analyze", body)
            .then(res => successCallback(res.data))
            .catch(err => failCallback(err));
    };

    // FE-10: one free-text answer -> values for the open findings it addresses.
    // body: { record, visitId?, findings, text } -> {answers:[{rule_id, target_path, value,
    // evidence_quote}], unanswered:[rule_id]}. Nothing is written server-side; the panel
    // shows what came back for review like any other answer.
    answerAll = (body, successCallback = () => {}, failCallback = () => {}) => {
        instance.post("/emr/answer", body)
            .then(res => successCallback(res.data))
            .catch(err => failCallback(err));
    };

    // EMR json -> CPT/ICD recommendations (or {unavailable, required_fields} when the
    // visit lacks the inputs the recommender needs).
    // body: { record, visitId? }
    recommend = (body, successCallback = () => {}, failCallback = () => {}) => {
        instance.post("/emr/recommend", body)
            .then(res => successCallback(res.data))
            .catch(err => failCallback(err));
    };

    // One recorded audio segment -> {text}. Whisper runs behind the recommender
    // (the service holds the speech key; the browser never sees it). 503 means the
    // server has no transcription configured — Dictation falls back to Web Speech.
    // The request ids of the calls behind what is currently on screen, newest value per
    // endpoint. Passed to feedback() so the verdict joins those log lines.
    requestIds = (urls = ["/emr/compose", "/emr/analyze", "/emr/recommend"]) =>
        urls.map(u => lastRequestIds[u]).filter(Boolean);

    // The clinician's verdict on what the panel produced: {rating:"up"|"down", comment,
    // surface, visitId, mrn, request_ids, snapshot, ...}. Logged, not stored: it lands in
    // the recommender's JSONL event log next to the calls it judges.
    feedback = (body, successCallback = () => {}, failCallback = () => {}) => {
        instance.post("/feedback", body)
            .then(res => successCallback(res.data))
            .catch(err => failCallback(err));
    };

    transcribe = (blob, successCallback = () => {}, failCallback = () => {}) => {
        const form = new FormData();
        form.append("audio", blob, "chunk.webm");
        instance.post("/transcribe", form)
            .then(res => successCallback(res.data))
            .catch(err => failCallback(err));
    };
}

export default new RecommenderService();
